import React, { useState } from 'react';
import './DragDrop.css';

function DragDrop({ pregunta, respuestas, onSelectDropZone, disabled, showCorrectAnswer = false, respuestaCorrecta = null }) {
  // Los hooks deben estar siempre al inicio, antes de cualquier return condicional
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [hoveredZoneId, setHoveredZoneId] = useState(null);

  // Validación de seguridad (después de los hooks)
  if (!pregunta || !pregunta.id) {
    return (
      <div className="dragdrop-container">
        <div className="dragdrop-error">
          <p>Error: Invalid question.</p>
        </div>
      </div>
    );
  }

  // Obtener los datos de drag_drop_data
  const dragDropData = pregunta.drag_drop_data || {};
  const draggableItems = dragDropData.draggable_items || [];
  const dropZones = (dragDropData.drop_zones || []).sort((a, b) => (a.orden || 0) - (b.orden || 0));

  // Si no hay datos, mostrar mensaje
  if (!dragDropData.draggable_items || !dragDropData.drop_zones || draggableItems.length === 0 || dropZones.length === 0) {
    return (
      <div className="dragdrop-container">
        <div className="dragdrop-error">
          <p>Error: This question does not have drag & drop data configured correctly.</p>
        </div>
      </div>
    );
  }

  // Obtener respuesta actual para esta pregunta
  const respuestaDragDrop = respuestas[pregunta.id] || {};

  // Manejar inicio del arrastre
  const handleDragStart = (e, itemId) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId.toString());
  };

  // Manejar cuando se arrastra sobre una zona de destino
  const handleDragOver = (e, zoneId) => {
    if (disabled) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setHoveredZoneId(zoneId);
  };

  // Manejar cuando se sale de una zona
  const handleDragLeave = () => {
    setHoveredZoneId(null);
  };

  // Manejar cuando se suelta en una zona de destino
  const handleDrop = (e, dropZoneId) => {
    if (disabled || !draggedItemId) return;
    
    e.preventDefault();
    const draggedId = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (!isNaN(draggedId)) {
      onSelectDropZone(pregunta.id, dropZoneId, draggedId);
    }
    
    setDraggedItemId(null);
    setHoveredZoneId(null);
  };

  // Manejar cuando se arrastra fuera de las zonas de destino
  const handleDragEnd = () => {
    setDraggedItemId(null);
    setHoveredZoneId(null);
  };

  // Obtener el item arrastrable que está en una zona de destino específica
  const getItemInDropZone = (dropZoneId) => {
    const itemId = respuestaDragDrop[`drop_zone_${dropZoneId}`];
    if (!itemId) return null;
    return draggableItems.find(item => item.id === itemId);
  };

  // Verificar si un item puede usarse múltiples veces (es respuesta correcta en múltiples drop_zones)
  const canItemBeUsedMultipleTimes = (itemId) => {
    const correctZonesCount = dropZones.filter(zone => zone.respuesta_correcta_id === itemId).length;
    return correctZonesCount > 1;
  };

  // Verificar si un item está siendo usado en alguna zona de destino
  // Si el item puede usarse múltiples veces, no lo bloqueamos
  const isItemUsed = (itemId) => {
    if (canItemBeUsedMultipleTimes(itemId)) {
      // Si puede usarse múltiples veces, nunca está "bloqueado"
      return false;
    }
    // Si solo puede usarse una vez, verificar si ya está en uso
    return Object.values(respuestaDragDrop).includes(itemId);
  };

  // Manejar eliminación de un item de una zona de destino (doble clic)
  const handleRemoveFromDropZone = (dropZoneId) => {
    if (disabled) return;
    onSelectDropZone(pregunta.id, dropZoneId, null);
  };

  return (
    <div className="dragdrop-container">
      <div className="dragdrop-wrapper">
        {/* Columna izquierda: Items arrastrables */}
        <div className="dragdrop-source-column">
          <div className="dragdrop-column-header">
            <h3>{dragDropData.titulo_source_column || "Services"}</h3>
          </div>
          <div className="dragdrop-items-list">
            {draggableItems.map((item) => {
              const used = isItemUsed(item.id);
              const isDragging = draggedItemId === item.id;
              const canUseMultiple = canItemBeUsedMultipleTimes(item.id);
              const isCurrentlyInUse = Object.values(respuestaDragDrop).includes(item.id);
              
              return (
                <div
                  key={item.id}
                  className={`dragdrop-item ${used && !canUseMultiple ? 'dragdrop-item-used' : ''} ${isDragging ? 'dragdrop-item-dragging' : ''}`}
                  draggable={!disabled && !used}
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  onDragEnd={handleDragEnd}
                  title={used && !canUseMultiple ? 'Already in use' : canUseMultiple && isCurrentlyInUse ? 'Can be used multiple times' : 'Drag to drop'}
                >
                  <div className="dragdrop-item-content">
                    <span className="dragdrop-item-text">{item.texto}</span>
                    {used && !canUseMultiple && <span className="dragdrop-item-used-badge">✓</span>}
                    {canUseMultiple && isCurrentlyInUse && <span className="dragdrop-item-multiple-badge" title="This item can be used in multiple zones">↻</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Columna derecha: Zonas de destino */}
        <div className="dragdrop-target-column">
          <div className="dragdrop-column-header">
            <h3>Answer Area</h3>
          </div>
          <div className="dragdrop-zones-list">
            {dropZones.map((zone) => {
              const itemInZone = getItemInDropZone(zone.id);
              const hasItem = itemInZone !== null;
              const correctItemId = showCorrectAnswer && respuestaCorrecta ? respuestaCorrecta[`drop_zone_${zone.id}`] : null;
              const correctItem = correctItemId != null ? draggableItems.find((i) => i.id === correctItemId) : null;
              const esCorrecto = correctItemId != null && itemInZone?.id === correctItemId;
              const esIncorrecto = showCorrectAnswer && correctItemId != null && hasItem && itemInZone?.id !== correctItemId;
              const zoneFeedbackClass = showCorrectAnswer && correctItemId != null
                ? esCorrecto
                  ? ' dragdrop-zone-correct'
                  : esIncorrecto
                  ? ' dragdrop-zone-incorrect'
                  : ' dragdrop-zone-sin-responder'
                : '';

              return (
                <div
                  key={zone.id}
                  className={`dragdrop-zone ${hasItem ? 'dragdrop-zone-filled' : 'dragdrop-zone-empty'}${zoneFeedbackClass}`}
                  onDragOver={!showCorrectAnswer ? (e) => handleDragOver(e, zone.id) : undefined}
                  onDragLeave={!showCorrectAnswer ? handleDragLeave : undefined}
                  onDrop={!showCorrectAnswer ? (e) => handleDrop(e, zone.id) : undefined}
                >
                  {zone.etiqueta && (
                    <div className="dragdrop-zone-label">
                      {zone.etiqueta}
                    </div>
                  )}
                  <div
                    className={`dragdrop-zone-slot ${hasItem ? 'dragdrop-zone-slot-filled' : ''} ${!showCorrectAnswer && hoveredZoneId === zone.id && !hasItem ? 'drag-over' : ''} ${showCorrectAnswer && correctItem ? 'dragdrop-zone-slot-with-feedback' : ''}`}
                    onDoubleClick={!showCorrectAnswer && disabled === false ? () => handleRemoveFromDropZone(zone.id) : undefined}
                    title={showCorrectAnswer ? undefined : (hasItem ? 'Double click to remove' : 'Drag an item here')}
                  >
                    {hasItem ? (
                      <div className="dragdrop-zone-item">
                        <span className="dragdrop-zone-item-text">{itemInZone.texto}</span>
                        {!showCorrectAnswer && !disabled && (
                          <button
                            className="dragdrop-zone-remove"
                            onClick={() => handleRemoveFromDropZone(zone.id)}
                            type="button"
                          >
                            ×
                          </button>
                        )}
                        {showCorrectAnswer && esIncorrecto && <span className="dragdrop-zone-feedback-incorrect">✗</span>}
                        {showCorrectAnswer && esCorrecto && <span className="dragdrop-zone-feedback-correct">✓</span>}
                      </div>
                    ) : (
                      <div className="dragdrop-zone-placeholder">
                        <span className="dragdrop-zone-placeholder-text">Table</span>
                      </div>
                    )}
                    {showCorrectAnswer && correctItem && (!hasItem || esIncorrecto) && (
                      <div className="dragdrop-zone-correct-answer">Correct: <strong>{correctItem.texto}</strong></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DragDrop;

